import React, { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Loader2 } from 'lucide-react'
import { useAppResumeRecovery } from './hooks/useAppResumeRecovery'
// Login loads eagerly — it's the first paint for every visitor and must not
// wait on a second round-trip. Everything else is lazy so the initial bundle
// stays small for players on mobile data.
import Login from './pages/Login'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const Play = lazy(() => import('./pages/Play'))
const Leaderboard = lazy(() => import('./pages/Leaderboard'))
const Help = lazy(() => import('./pages/Help'))
const Staff = lazy(() => import('./pages/Staff'))
const Voting = lazy(() => import('./pages/Voting'))
const Raffle = lazy(() => import('./pages/Raffle'))
const DisplayLeaderboard = lazy(() => import('./pages/DisplayLeaderboard'))
const DisplayCostume = lazy(() => import('./pages/DisplayCostume'))
const DisplayHole = lazy(() => import('./pages/DisplayHole'))
const ScoreSubmitTest = lazy(() => import('./pages/ScoreSubmitTest'))

function RouteFallback() {
  return (
    <div
      className="min-h-[60dvh] flex items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 text-white">
        <Loader2 className="w-6 h-6 animate-spin text-neon-cyan" aria-hidden />
        <span className="font-setlist text-sm uppercase tracking-wider">
          Loading…
        </span>
      </div>
    </div>
  )
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-950 text-white">
      <div className="max-w-md w-full bg-slate-900 border-2 border-neon-cyan rounded-lg p-6 text-center shadow-neon">
        <p className="font-setlist text-sm uppercase tracking-wider text-neon-cyan mb-2">
          Wrong Fairway
        </p>
        <h1 className="font-poster text-4xl text-white mb-3">Page Not Found</h1>
        <p className="font-specs text-gray-300 mb-6">
          That link is not part of the course. Head back to the app and keep playing.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/dashboard"
            className="px-4 py-3 rounded-lg border-2 border-neon-cyan text-neon-cyan font-setlist uppercase font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            Scorecard
          </Link>
          <Link
            to="/login"
            className="px-4 py-3 rounded-lg border-2 border-white bg-gradient-psych text-white font-setlist uppercase font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            Log In
          </Link>
        </div>
      </div>
    </div>
  )
}

function App() {
  // Maps / app-switch white-screen recovery: `useAppResumeRecovery` (bfcache,
  // return after opening directions, compositor nudge on every resume).
  useAppResumeRecovery()

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-900">
          <div className="max-w-md w-full bg-slate-800 border-2 border-red-500 rounded-lg p-6 text-center">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Something went wrong</h1>
            <p className="text-slate-300 mb-4">
              We've been notified and are looking into it. Please try refreshing the page.
            </p>
            <button
              onClick={resetError}
              className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg font-bold hover:bg-neon-lime transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      showDialog={false}
    >
      <Router>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Login />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/play/:holeId" element={<Play />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/help" element={<Help />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/admin" element={<Navigate to="/staff" replace />} />
            <Route path="/voting" element={<Voting />} />
            <Route path="/raffle" element={<Raffle />} />
            <Route path="/display/leaderboard" element={<DisplayLeaderboard />} />
            <Route path="/display/costume" element={<DisplayCostume />} />
            <Route path="/display/hole" element={<DisplayHole />} />
            <Route path="/qr/score-test" element={<ScoreSubmitTest />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </Sentry.ErrorBoundary>
  )
}

export default App
